/**
 * ============================================================================
 * ANALYTICS CARD COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Reusable card component for displaying analytics metrics
 */

import React from 'react';
import Card from '../common/Card';
import { useI18n } from '../../providers';

/**
 * Analytics Card Component
 * @param {Object} props
 * @param {string} props.title - Card title
 * @param {string|number} props.value - Main value to display
 * @param {string} props.icon - Icon emoji
 * @param {string} props.color - Gradient color classes
 * @param {string} props.trend - Trend indicator (up, down, neutral)
 * @param {string|number} props.trendValue - Trend value
 * @param {string} props.description - Optional description
 * @param {React.ReactNode} props.children - Optional children content
 * @returns {JSX.Element}
 */
const AnalyticsCard = ({
  title,
  value,
  icon,
  color = 'from-blue-500 to-cyan-500',
  trend,
  trendValue,
  description,
  children,
}) => {
  const { language } = useI18n();

  const getTrendIcon = () => {
    if (trend === 'up') return '📈';
    if (trend === 'down') return '📉';
    return '➡️';
  };

  const getTrendColor = () => {
    if (trend === 'up') return 'text-green-600 dark:text-green-400';
    if (trend === 'down') return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  return (
    <Card
      variant="glass"
      hover
      className="animate-fadeInUp bg-gradient-to-br from-white/80 to-white/40 dark:from-gray-800/80 dark:to-gray-900/40"
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-2xl shadow-lg`}>
              {icon}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
              {description && (
                <p className="text-xs text-gray-500 dark:text-gray-500">{description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Value */}
        <div>
          <p className="text-3xl font-black text-gray-900 dark:text-white mb-2">{value}</p>
          {trend && trendValue !== undefined && (
            <div className={`flex items-center gap-1 text-sm font-bold ${getTrendColor()}`}>
              <span>{getTrendIcon()}</span>
              <span>{trendValue}</span>
            </div>
          )}
        </div>

        {/* Children Content */}
        {children && <div className="pt-2">{children}</div>}
      </div>
    </Card>
  );
};

export default AnalyticsCard;
